import useActionAllowedInArea from "entities/auth/hooks/useActionAllowedInArea";
import { useFreeToPlayMainPage } from "entities/brands/queries/useFreeToPlayMainPage";
import { useBrandsListQuery } from "entities/brands/queries/usePagesListByBrandQuery";
import { useMemo } from "react";
import { RouteType } from "shared/routes/types";
import { getDynamicRoutesWithBrands } from "shared/routes/utils";
import {
  BRAND_TAB_PATH,
  BROADCASTS_PATH,
  BULLETINS_PATH,
  CUSTOMER_SERVICES_CENTRE,
  FAQ_GROUP_PATH,
  GAMES_PATH,
  TAGS_AND_METADATA_PATH,
  MODULES_PATH,
  ROLES_AND_PERMISSIONS,
  STUDIOS_PATH,
  TRANSLATIONS_PATH,
  USERS_PATH,
  MANAGE_ARTICLES_PATH,
  CONTENT_SCHEDULE_PATH,
} from "../routes/paths";

export const useDynamicRouting = () => {
  const { data: brands = [], isLoading: isLoadingBrands } =
    useBrandsListQuery();
  const { data: freeToPlayMainPage } = useFreeToPlayMainPage();

  const { allowed: allowedBrandsAndPages, loading: loadingBrandsAndPages } =
    useActionAllowedInArea("brandsAndPages", "read");
  const { allowed: allowedTags, loading: loadingTags } = useActionAllowedInArea(
    "tags",
    "read",
  );
  const { allowed: allowedStudios, loading: loadingStudios } =
    useActionAllowedInArea("studios", "read");
  const { allowed: allowedModules, loading: loadingModules } =
    useActionAllowedInArea("moduleTemplates", "read");
  const { allowed: allowedGames, loading: loadingGames } =
    useActionAllowedInArea("games", "read");
  const { allowed: allowedTranslations, loading: loadingTranslations } =
    useActionAllowedInArea("translations", "read");
  const { allowed: allowedRolesPermissions, loading: loadingRolesPermissions } =
    useActionAllowedInArea("rolesAndPermissions", "read");
  const { allowed: allowedManageArticles, loading: loadingManageArticles } =
    useActionAllowedInArea("legalDocuments", "read");

  const { allowed: allowedContentSchedule, loading: loadingContentSchedule } =
    useActionAllowedInArea("contentSchedule", "read");

  const { allowed: allowedFaq, loading: loadingFaq } = useActionAllowedInArea(
    "faq",
    "read",
  );
  const { allowed: allowedBulletin, loading: loadingBulletin } =
    useActionAllowedInArea("bulletinBoard", "read");

  const {
    allowed: allowedCustomerServicesCentre,
    loading: loadingCustomerServicesCentre,
  } = useActionAllowedInArea("customerServicesCentre", "read");

  const { allowed: allowedUsers, loading: loadingUsers } =
    useActionAllowedInArea("users", "read");
  const { allowed: allowedBroadcasts, loading: loadingBroadcasts } =
    useActionAllowedInArea("users", "read");

  const isLoadingRoutes =
    isLoadingBrands ||
    loadingBrandsAndPages ||
    loadingTags ||
    loadingStudios ||
    loadingModules ||
    loadingGames ||
    loadingTranslations ||
    loadingFaq ||
    loadingBulletin ||
    loadingRolesPermissions ||
    loadingCustomerServicesCentre ||
    loadingBroadcasts ||
    loadingUsers ||
    loadingManageArticles ||
    loadingContentSchedule;

  const { dynamicRoutes, allowedRoutes } = useMemo(() => {
    const dynamicRoutes = getDynamicRoutesWithBrands(
      brands,
      freeToPlayMainPage?.id ?? "",
    );

    const allowedRoutes = dynamicRoutes
      .map((route) => {
        const isRouteForbidden =
          (route.pathname === BRAND_TAB_PATH && !allowedBrandsAndPages) ||
          (route.pathname === TAGS_AND_METADATA_PATH && !allowedTags) ||
          (route.pathname === STUDIOS_PATH && !allowedStudios) ||
          (route.pathname === MODULES_PATH && !allowedModules) ||
          (route.pathname === GAMES_PATH && !allowedGames) ||
          (route.pathname === TRANSLATIONS_PATH && !allowedTranslations) ||
          (route.pathname === USERS_PATH && !allowedUsers) ||
          (route.pathname === ROLES_AND_PERMISSIONS &&
            !allowedRolesPermissions) ||
          (route.pathname === BROADCASTS_PATH && !allowedBroadcasts) ||
          (route.pathname === CUSTOMER_SERVICES_CENTRE &&
            !allowedCustomerServicesCentre) ||
          (route.pathname === MANAGE_ARTICLES_PATH && !allowedManageArticles) ||
          (route.pathname === CONTENT_SCHEDULE_PATH && !allowedContentSchedule);

        if (isRouteForbidden) {
          return null;
        }

        if (route.elements?.length) {
          const filteredElements = route.elements.filter((el) => {
            const disallowedRoute =
              (el.pathname === FAQ_GROUP_PATH && !allowedFaq) ||
              (el.pathname === BULLETINS_PATH && !allowedBulletin);
            return !disallowedRoute;
          });

          if (!filteredElements.length) {
            return null;
          }

          return {
            ...route,
            elements: filteredElements,
          };
        }

        return route;
      })
      .filter(Boolean) as RouteType[];

    return { dynamicRoutes, allowedRoutes };
  }, [
    brands,
    freeToPlayMainPage?.id,
    allowedBrandsAndPages,
    allowedTags,
    allowedStudios,
    allowedModules,
    allowedGames,
    allowedTranslations,
    allowedUsers,
    allowedRolesPermissions,
    allowedBroadcasts,
    allowedCustomerServicesCentre,
    allowedManageArticles,
    allowedFaq,
    allowedBulletin,
    allowedContentSchedule,
  ]);

  // Define initialRoute that is the first element in navigation list
  const initialPathname = useMemo(
    () =>
      allowedRoutes[0]?.elements?.[0]?.pathname || allowedRoutes[0]?.pathname,
    [allowedRoutes],
  );

  return {
    dynamicRoutes,
    allowedRoutes,
    isLoadingRoutes,
    initialPathname,
    freeToPlayMainPageId: freeToPlayMainPage?.id,
  };
};
